import { createEffect, type JSX, onCleanup, onMount, Show } from 'solid-js';
import { t } from '../i18n';
import { squareText } from '../lib/fleet-map';
import type { UtilBox } from '../lib/fleetUtilization';

// The Machines Map's one tooltip (#6562, prototype `#tip`): the machine's name, `<8-char id>
// · <server>`, "From <org>" for another org's machine, health and status, then CPU / Memory
// / Disk. Presenter: whose it is and when it closes are `lib/map-tip.ts`'s, wired by
// `MachineMap`; this places it and reports the pointer on it.
//
// FIXED TO THE VIEWPORT, so the panel's clipping cannot cut it: under its square, shifted
// left at the right edge, above the square when there is no room below. It reads its
// square's box afresh on every scroll and resize, so it follows the square: a Tab onto a
// square below the fold scrolls the page AFTER the focus that opened the tooltip, and a
// tooltip that closed on scroll lost that square's details.

/**
 * Between the square and its tooltip, and the least room kept to the viewport's edge. The
 * hover bridge in `styles/views/_fleet-map.scss` is GAP plus the tooltip's 1px border (7px):
 * change one, change the other.
 */
const GAP = 6;
const EDGE = 8;

export function MachineMapTip(props: {
  readonly id: string;
  readonly box: UtilBox;
  /** The square it describes. */
  readonly anchor: HTMLElement;
  /** The pointer on the tooltip holds it open (WCAG 1.4.13 hoverable). */
  readonly onPointerEnter: () => void;
  readonly onPointerLeave: () => void;
}): JSX.Element {
  let el: HTMLDivElement | undefined;
  const text = () => squareText(props.box, t);
  const place = (): void => {
    if (el === undefined) return;
    const a = props.anchor.getBoundingClientRect();
    const { offsetWidth: w, offsetHeight: h } = el;
    const below = a.bottom + GAP;
    const above = a.top - GAP - h;
    const fits = below + h <= window.innerHeight - EDGE || above < EDGE;
    el.style.left = `${Math.max(EDGE, Math.min(a.left, window.innerWidth - w - EDGE))}px`;
    el.style.top = `${fits ? below : above}px`;
    // Which side the gap bridge (`::before`, `_fleet-map.scss`) spans toward the square.
    el.dataset['side'] = fits ? 'below' : 'above';
  };
  createEffect(() => {
    props.anchor;
    text();
    place();
  });
  onMount(() => {
    window.addEventListener('scroll', place, { capture: true, passive: true });
    window.addEventListener('resize', place, { passive: true });
    onCleanup(() => {
      window.removeEventListener('scroll', place, { capture: true });
      window.removeEventListener('resize', place);
    });
  });
  return (
    <div
      id={props.id}
      ref={el}
      role="tooltip"
      class="machine-map__tip"
      onPointerEnter={() => props.onPointerEnter()}
      onPointerLeave={() => props.onPointerLeave()}
    >
      <p>
        <strong>{text().title}</strong> <span class="machine-map__tip-sub">{text().where}</span>
      </p>
      <Show when={props.box.via_org}>
        {(org) => <p>{t('fleet.machines.from_org', { org: org() })}</p>}
      </Show>
      <p>{text().state}</p>
      <p class="machine-map__tip-readings">{text().readings}</p>
    </div>
  );
}
